import { ApplicationRef, ComponentRef, createComponent, Injectable, Injector } from '@angular/core';
import { Cluster, Marker, MarkerClusterer } from '@googlemaps/markerclusterer';
import { MarkerStopComponent } from 'src/app/components/markers';
import { ServiceTagComponent } from 'src/app/components/service-tag/service-tag.component';
import { Colors, NotificationTypes } from 'src/app/models/enums';
import { environment } from 'src/environments/environment';
import { images } from 'src/images';
import { GeoJsonObject, ILatLngPoint, IRouteChunk, IServiceInfo, ITripPosition } from '../../models';
import { NotificationService } from '../notification/notification.service';
import { UtilsService } from '../utils/utils.service';
import { ITripDrivenRoute } from 'src/app/models/trip/tripDrivenRoute';
import { LiteralService } from '../literal/literal.service';
import moment from 'moment';
import { APP } from 'src/app/config/app';

@Injectable({
  providedIn: 'root',
})
export class MapUtilsService {
  public markersGlobal: any[] = [];
  public stopCircles: google.maps.Circle[] = [];
  private stopCirclesVisible = false;
  public images = images;
  public polygons: google.maps.Polygon[] = [];
  public clusterStopsMarkersGlobal: any[] = [];
  public markerClusterer: MarkerClusterer;
  public map: google.maps.Map;
  public infoWindows: google.maps.InfoWindow[] = [];
  private stopTagComponentRefs: ComponentRef<ServiceTagComponent>[] = [];

  public positionsMarkerClusterer: MarkerClusterer;
  public clusterPositionsMarkersGlobal: any[] = [];
  public positionsPolygons: any = null;

  constructor(
    private utilsService: UtilsService,
    private notificationService: NotificationService,
    private injector: Injector,
    private applicationRef: ApplicationRef,
    private literalService: LiteralService
  ) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.map) {
        this.showOutlines(true, this.map);
      }
    });
  }

  async addMarker(
    map: google.maps.Map,
    marker: ITripPosition,
    image: string,
    clear: boolean = false,
    zIndex?: number,
    onMarkerClick?: (tripId: number) => void
  ) {
    if (clear) {
      this.markersGlobal.map((map: any) => (map.map = null));
      return;
    }
    const { AdvancedMarkerElement, PinElement } =
      (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    let img = '';
    await this.utilsService.getSvgImageData(image).then((res: any) => {
      img = res;
    });

    const parser = new DOMParser();
    const pinSvgElement = parser.parseFromString(
      img,
      'image/svg+xml',
    ).documentElement;

    const pin = new PinElement({
      scale: 1.5,
      background: '#FFF',
      borderColor: marker.color && marker.color,
      glyph: pinSvgElement,
    });

    const vehicleId = marker.vehicleId;
    const title = vehicleId ? vehicleId.toString() : '';

    // Remove old ones
    const markerToRemove = this.markersGlobal.find((mkr: any) => title != '' && mkr.title == title);
    if (markerToRemove) {
      markerToRemove.setMap(null); // This removes the marker from the map
      // Optionally, remove the marker object from the markersGlobal array
      this.markersGlobal = this.markersGlobal.filter(marker => marker !== markerToRemove);
    }

    const newMarker = new AdvancedMarkerElement({
      map: map,
      position: { lat: marker.latitude, lng: marker.longitude },
      content: pin.element,
      title: title,
      zIndex: zIndex ?? 40
    });
    if (onMarkerClick) {
      newMarker.addEventListener('gmp-click', () => {
        onMarkerClick(marker.tripId);
      });
    }

    this.markersGlobal.push(
      newMarker
    );
  };

  async addCustomMarkers(
    map: google.maps.Map,
    markers?: any[],
    clear: boolean = false,
    onStopClick?: (stop: any) => void
  ) {
    if (clear) {
      this.clearMarkers();
      this.clearInfoWindows();
      return;
    }
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    
    this.clearMarkers();
    this.clearInfoWindows();

    markers!.map((marker: any) => {
      const mark = new AdvancedMarkerElement({
        map,
        position: { lat: marker.latitude, lng: marker.longitude },
        content: marker.element,
        zIndex: marker.zIndex ?? 40,
        gmpClickable: true,
      });

      if (marker.radius && marker.radius > 0) {
        this.stopCircles.push(new google.maps.Circle({
          map: this.stopCirclesVisible ? map : null,
          center: { lat: marker.latitude, lng: marker.longitude },
          radius: marker.radius,
          fillColor: marker.color,
          fillOpacity: 0.2,
          strokeWeight: 0,
          clickable: false,
        }));
      }

      const infoWindow = new google.maps.InfoWindow({ disableAutoPan: true });
      let contentBuilt = false;
      mark.addEventListener('gmp-click', () => {
        if (!contentBuilt) {
          infoWindow.setContent(this.createSelectableStopInfoWindow(marker, onStopClick));
          contentBuilt = true;
        }
        if (marker.start || marker.end) infoWindow.open(map, mark);
        else {
          infoWindow.open(map);
          infoWindow.setPosition({
            lat: marker.latitude,
            lng: marker.longitude
          });
        }
        this.infoWindows.push(infoWindow);
      });
      if (marker.start || marker.end) {
        if (marker.start) mark.setAttribute('start', `1`);
        if (marker.end) mark.setAttribute('end', `1`);
        mark.addEventListener("mouseover", () => {
          mark.zIndex = marker.end ? 50 : 30;
        })
        mark.addEventListener("mouseout", () => {
          mark.zIndex = marker.end ? 30 : 50;
        })
      }
      this.markersGlobal.push(mark);
    });
  };

  async setUserCurrentPosition(map: google.maps.Map, image: string) {
    const { AdvancedMarkerElement, PinElement } =
      (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position: GeolocationPosition) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          let img = '';
          await this.utilsService.getSvgImageData(image).then((res: any) => {
            img = res;
          });

          const parser = new DOMParser();
          const pinSvgElement = parser.parseFromString(
            img,
            'image/svg+xml',
          ).documentElement;

          const pin = new PinElement({
            scale: 1.5,
            background: '#FFF',
            glyph: pinSvgElement,
          });

          new AdvancedMarkerElement({
            map: map,
            position: pos,
            content: pin.element,
          });
        },
        () => {
          this.handleLocationError();
        },
      );
    } else {
      // Browser doesn't support Geolocation
      this.handleLocationError();
    }
  };

  handleLocationError() {
    this.notificationService.image = images.sidebar.users;
    this.notificationService.title = 'Location user error';
    this.notificationService.message =
      'Error getting the user current position';
    this.notificationService.show(NotificationTypes.WARNING);
  };

  drawOutlines(map: google.maps.Map, serviceInfos: IServiceInfo[], clickEventListener?: any, clickable?: boolean) {
    this.clearOutlines();
    this.map = map;
    serviceInfos
    .map((serviceInfo: IServiceInfo) => {
      if (serviceInfo.outlines && serviceInfo.outlines.length > 0) {
        serviceInfo.outlines.filter((outline) => outline?.geometry?.coordinates?.[0]).map((outline, index) => {
          const coords: any[] = [];
          outline.geometry.coordinates[0].map((coord: any) => {
            coords.push({ lat: coord[1], lng: coord[0] });
          });
          const outlinePolygon = new google.maps.Polygon({
            paths: coords,
            strokeColor: serviceInfo.color || Colors.TRANSPARENT,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: serviceInfo.color || Colors.TRANSPARENT,
            fillOpacity: 0.10,
            clickable: clickable ?? false
          });
          outlinePolygon.set('outlineId', `${serviceInfo.id}-${index}`);
          outlinePolygon.setMap(map);
          if (clickEventListener) {
            outlinePolygon.addListener('click', (event: any) => {
              clickEventListener(event);
            });
          }
          this.polygons.push(outlinePolygon);
        })
      }
    });
  }

  drawOutlinesFromOutlines(map: google.maps.Map, outlines: any[]) {
    this.clearOutlines();
    this.map = map;
    
    outlines.forEach((outline: GeoJsonObject) => {
      const coords: any[] = [];
      outline.geometry?.coordinates[0].map((coord: any) => {
        coords.push({ lat: coord[1], lng: coord[0] });
      });
      const outlinesPolygon = new google.maps.Polygon({
        paths: coords,
        strokeColor: outline.properties?.outlineColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: outline.properties?.outlineColor,
        fillOpacity: 0.10,
      });
      outlinesPolygon.setMap(map);
      this.polygons.push(outlinesPolygon);
    });
  }

  showOutlines(show: boolean, map: google.maps.Map, outlinesIds?: string[]) {
    if (!outlinesIds) outlinesIds = this.polygons.map((polygon: google.maps.Polygon) => polygon.get('outlineId'));
    this.polygons.forEach((polygon: google.maps.Polygon) => {
      polygon.setMap(null);
      if (show && outlinesIds.includes(polygon.get('outlineId'))) polygon.setMap(map);
    });
  };

  clearOutlines() {
    this.polygons.forEach((polygon: google.maps.Polygon) => {
      polygon.setMap(null); // Remove polygon from map
    });
    this.polygons = []; // Clear the array
  };

  isPointInsideOutlines(lat: number, lng: number) {
    let isInside = false;
    this.polygons.map((polygon) => {
      let isPointInsidePolygon = google.maps.geometry.poly.containsLocation(new google.maps.LatLng(lat, lng), polygon);
      if (isPointInsidePolygon) isInside = true;
    });
    return isInside;
  }

  getOutlineIdsFromCoordinates(lat: number, lng: number) {
    let outlineIds: any[] = [];
    this.polygons.map((polygon) => {
      if (google.maps.geometry.poly.containsLocation(new google.maps.LatLng(lat, lng), polygon)) outlineIds.push(polygon.get('outlineId'));
    })
    return outlineIds;
  }

  checkIfStopsAreSameOutline(origin: google.maps.LatLngLiteral, destination: google.maps.LatLngLiteral) {
    if (origin && destination) {
      const originOutlineIds = this.getOutlineIdsFromCoordinates(origin.lat, origin.lng);
      const destinationOutlineIds = this.getOutlineIdsFromCoordinates(destination.lat, destination.lng);
      return this.utilsService.getArraysIntersection(originOutlineIds, destinationOutlineIds).length > 0;
    }
    return true;
  }

  async addStopsClustersMarkers(map: google.maps.Map, stops: any[], onStopClick?: (stop: any) => void, showInfoWindow: boolean = true) {
    this.clearClustersMarkers();
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    const markers = stops.map((stop) => {

      const disabled = !stop.available;

      const marker = new AdvancedMarkerElement({
        position: stop,
        content: stop.marker,
        zIndex: disabled ? 1 : 2
      });
      (marker as any).stopColor = stop.color || '#000000';
      (marker as any).available = stop.available;
      
      if (showInfoWindow) {
        const infoWindow = new google.maps.InfoWindow();
        let contentBuilt = false;
        // markers can only be keyboard focusable when they have click listeners
        // open info window when marker is clicked
        marker.addListener("click", () => {
          if (!contentBuilt) {
            infoWindow.setContent(this.createSelectableStopInfoWindow(stop, onStopClick));
            contentBuilt = true;
          }
          infoWindow.open(map, marker);
          this.infoWindows.push(infoWindow);
        });

        map.addListener("click", () => infoWindow.close());
        this.polygons.map((polygon: google.maps.Polygon) => polygon.addListener("click", () => infoWindow.close()));
      }
      this.clusterStopsMarkersGlobal.push(marker);
  
      return marker;
    });

    const renderCluster = (cluster: Cluster): Marker => {
      const componentRef = createComponent(MarkerStopComponent, {
        environmentInjector: this.applicationRef.injector,
        elementInjector: this.injector
      });
  
      const available = cluster.markers?.some((marker) => (marker as any).available);
      const stopColor = (cluster.markers[0] as any).stopColor || '#000000';
      componentRef.setInput('color', available ? stopColor : '#848ea8');
      componentRef.setInput('count', cluster.count);
      componentRef.setInput('scale', Math.min(1 + cluster.count / 10, 1.5 ))
      componentRef.changeDetectorRef.detectChanges();
  
      const element = componentRef.location.nativeElement;
  
      return new google.maps.marker.AdvancedMarkerElement({
        position: cluster.position,
        title: String(cluster.count),
        content: element
      });
    }

    this.markerClusterer = new MarkerClusterer({map, markers, onClusterClick: this.onClusterClick, renderer: { render: (cluster) => renderCluster(cluster)}});
    
    map.addListener('zoom_changed', () => {
      this.setClustersZIndex();
    });

    this.setClustersZIndex();
    return markers
  };

  async addPositionsClustersAndRoutes(map: google.maps.Map, data: ITripDrivenRoute) {
    this.clearPositionsClusterAndRoutes();
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    const positionMarker = document.createElement('img');
    positionMarker.src = new URL(images.trips.drivenPosition, import.meta.url).href;
    positionMarker.style.width = '32px';
    positionMarker.style.height = '32px';

    const markers = data.positions.map((position) => {
      const infoWindow = new google.maps.InfoWindow();
      const infoWindowContent = `
        <div class="p-4 flex flex-col gap-2 text-base whitespace-nowrap">
          <div><span class="font-semibold">${this.literalService.get('trips.time', true)}</span> ${moment(this.utilsService.convertDate(position.dateTime)).format('HH:mm:ss')}</div>
          <div><span class="font-semibold">${this.literalService.get('trips.longitude', true)}</span> ${position.longitude}</div>
          <div><span class="font-semibold">${this.literalService.get('trips.latitude', true)}</span> ${position.latitude}</div>
        </div>
      `;
      infoWindow.setContent(infoWindowContent);

      
      const marker = new AdvancedMarkerElement({
        position: new google.maps.LatLng(position.latitude, position.longitude),
        content: positionMarker.cloneNode(),
        gmpClickable: true,
        zIndex: 1
      });
  
      marker.addEventListener("gmp-click", () => {
        infoWindow.open(map, marker);
        this.infoWindows.push(infoWindow);
      });

      map.addListener("click", () => infoWindow.close());
      this.clusterPositionsMarkersGlobal.push(marker);
  
      return marker;
    });

    const renderCluster = (cluster: Cluster): Marker => {
      return new google.maps.marker.AdvancedMarkerElement({
        position: cluster.position,
        title: String(cluster.count),
        content: positionMarker.cloneNode()
      });
    }

    this.positionsMarkerClusterer = new MarkerClusterer({map, markers, onClusterClick: this.onClusterClick, renderer: { render: (cluster) => renderCluster(cluster)}});
    
    map.addListener('zoom_changed', () => {
      this.setClustersZIndex();
    });

    this.positionsPolygons = new google.maps.Polyline({
      path: data.polyline,
      strokeColor: '#B30F17',
      strokeWeight: 2,
      zIndex: 70
    });
    this.positionsPolygons.setMap(map);

    this.setClustersZIndex();
    return markers
  };

  showClusterMarkers(show: boolean) {
    if (this.clusterStopsMarkersGlobal.length > 0) {
      if (show) {
        this.markerClusterer.addMarkers(this.clusterStopsMarkersGlobal);
      } else {
        this.markerClusterer.clearMarkers(false);
      }
    }
  };

  clearClustersMarkers() {
    this.clusterStopsMarkersGlobal.forEach((marker: any) => {
      marker.setMap(null);
    });
    if (this.clusterStopsMarkersGlobal.length > 0) this.markerClusterer.clearMarkers(false);
    this.clusterStopsMarkersGlobal = [];
  };

  clearPositionsClusterAndRoutes() {
    this.clusterPositionsMarkersGlobal.forEach((marker: any) => {
      marker.setMap(null);
    });
    if (this.clusterPositionsMarkersGlobal.length > 0) this.positionsMarkerClusterer.clearMarkers(false);
    this.clusterPositionsMarkersGlobal = [];
    if (this.positionsPolygons) {
      this.positionsPolygons.setMap(null);
      this.positionsPolygons = null;
    }
  };

  clearInfoWindows() {
    this.infoWindows.forEach((infoView) => {
      infoView.close();
    });
    this.infoWindows = [];
    this.stopTagComponentRefs.forEach((componentRef) => componentRef.destroy());
    this.stopTagComponentRefs = [];
  }

  clearMarkers() {
    this.markersGlobal.forEach(marker => {
      marker.setMap(null);
    })
    this.markersGlobal = [];
    this.stopCircles.forEach(circle => circle.setMap(null));
    this.stopCircles = [];
  }

  setStopCirclesVisibility(map: google.maps.Map | null) {
    this.stopCirclesVisible = !!map;
    this.stopCircles.forEach(circle => circle.setMap(map));
  }

  setClustersZIndex() {
    setTimeout(() => {
      const clusterElements = document.querySelectorAll('[aria-label*="markers"]');
      clusterElements.forEach((element: Node) => {
        if (element instanceof HTMLElement) {
          element.style.zIndex = '0';
        }
      });
      
      const markers = document.querySelectorAll('[class*="marker-view"]');
      markers.forEach((element: Node) => {
        if (element instanceof HTMLElement) {
          const start = element.getAttribute('start');
          const end = element.getAttribute('end');
          if (!start && !end) element.style.zIndex = '10';
        }
      });

      const odMarkers = Array.from(document.getElementsByTagName('app-marker-od'));
      odMarkers.forEach((element: Node) => {
        if (element.parentElement! instanceof HTMLElement) {
          element.parentElement!.style.zIndex = '40';
        }
      });
    }, 500);
  };

  onClusterClick(event: google.maps.MapMouseEvent, cluster: Cluster, map: google.maps.Map) {
    const bounds = cluster.bounds!;
    google.maps.event.addListenerOnce(map, 'zoom_changed', function() {
      if (map.getZoom()! > 18)
        map.setZoom(18);
    });

    const margin = 100;
    const paddingLeft = 600;
    const paddingTop = 10;
    const screenWidth = window.innerWidth - paddingLeft;
    const screenHeight = window.innerHeight - paddingTop;
    const left = paddingLeft + screenWidth * 0.5 - margin;
    const top = paddingTop + screenHeight * 0.5 - margin;
    const right = screenWidth * 0.5 - margin;
    const bottom = screenHeight * 0.5 - margin;

    const padding = { top: top, bottom: bottom, left: left, right: right };
    map.fitBounds(bounds, padding);
  };

  private createSelectableStopInfoWindow(stop: any, onStopClick?: any) {
    const container = document.createElement('div');
    // Google's info window gives its content almost no room, so the padding is ours to add
    container.className = 'flex items-center gap-2 px-1 py-0.5';

    const textColumn = document.createElement('div');
    textColumn.className = 'flex flex-col gap-0.5';

    const isAvailable = stop.available ?? true;

    // Without a click handler the stop is only being shown, not offered: no affordance, no action
    const selectable = isAvailable && !!onStopClick;

    const title = document.createElement('div');
    title.className = `font-semibold ${ selectable ? 'cursor-pointer' : ''}`;
    title.innerText = stop.name;
    if (selectable) {
      title.addEventListener('click', () => onStopClick(stop));
    }

    textColumn.appendChild(title);

    const serviceTags = this.createStopServiceTags(stop.serviceInfos);
    if (serviceTags) textColumn.appendChild(serviceTags);

    container.appendChild(textColumn);

    if (onStopClick) {
      const img = document.createElement('img');
      if (stop.demand) {
        img.src = isAvailable ? images.routes.stops.demandDefault : images.routes.stops.demandDisabled;
      } else {
        img.src = isAvailable ? images.stop : images.stopDisabled;
      }
      img.className = `w-6 h-6 shrink-0 ${ isAvailable ? 'cursor-pointer' : ''}`;
      img.addEventListener('click', () => {
        if (isAvailable) onStopClick(stop);
      });
      container.appendChild(img);
    }

    return container;
  }

  private createStopServiceTags(serviceInfos?: any[]): HTMLElement | null {
    if (!serviceInfos?.length) return null;
    const tagsRow = document.createElement('div');
    tagsRow.className = 'flex flex-wrap items-center gap-1';
    serviceInfos.forEach((serviceInfo: any) => {
      const componentRef = createComponent(ServiceTagComponent, {
        environmentInjector: this.applicationRef.injector,
        elementInjector: this.injector,
      });
      componentRef.setInput('serviceInfo', serviceInfo);
      componentRef.setInput('compact', true);
      componentRef.changeDetectorRef.detectChanges();
      this.stopTagComponentRefs.push(componentRef);
      tagsRow.appendChild(componentRef.location.nativeElement);
    });
    return tagsRow;
  }

  async getNearestRoad(lat: number, lng: number) {
    try {
      const response = await fetch(`https://roads.googleapis.com/v1/nearestRoads?points=${lat},${lng}&key=${environment.googleMapsApiKey}`);

      const resJSON = await response.json();
      const snappedPoint = resJSON.snappedPoints[0];

      return await this.getPlaceDetailGeocode(snappedPoint.location.latitude, snappedPoint.location.longitude);
    } catch (error) {
      // tslint:disable-next-line: no-console
      console.error(error);
      return [];
    }
  }

  async getPlaceDetailGeocode(lat: number, lng: number) {
    const geocoder = new google.maps.Geocoder();
    const geoCoderRequest = { location: new google.maps.LatLng(lat, lng) };
    const placeDetailsGeoResults = await geocoder.geocode(geoCoderRequest);
    const placeDetail = placeDetailsGeoResults.results.find((res: any) => res.types.includes('street_address') || res.types.includes('plus_code'));
    const addressComponents = placeDetail?.address_components;
    const streetName = addressComponents?.find((component: any) => component.types.includes('route') || component.types.includes('plus_code'));
    const streetNumber = addressComponents?.find((component: any) => component.types.includes('street_number'));
    const locality = addressComponents?.find((component: any) => component.types.includes('locality') || component.types.includes('political'));
    return {
      id: placeDetail?.place_id,
      location: {
        lat: lat,
        lng: lng
      },
      name: `${streetName?.long_name}${streetNumber ? `, ${streetNumber?.long_name}` : ''}${locality ? `, ${locality.long_name}` : ''}`
    }
  }

  async getPlaceLocation(placeId: string) {
    // @ts-ignore
    const { Place } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
    const place = new Place({
      id: placeId
    });
    await place.fetchFields({
      fields: [
        'displayName',
        'location'
      ]
    });
    return {
      id: place.id,
      location: {
        lat: place.location?.lat(),
        lng: place.location?.lng()
      },
      name: `${place.displayName}`
    }
  }

  async getPlaces(nameToSearch: string, originLat: number, originLng: number) {
    // @ts-ignore
    const { AutocompleteSuggestion } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

    const request: google.maps.places.AutocompleteRequest = {
      input: nameToSearch,
      includedPrimaryTypes: ['geocode'],
      locationBias: { lat: originLat, lng: originLng }
    };
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    let results: any[] = [];
    const placePrediction = suggestions[0].placePrediction?.toPlace();
    const placeDetails = await this.getPlaceLocation(placePrediction!.id);
    results.push({
      id: placeDetails?.id,
      name: placeDetails?.name,
      location: placeDetails.location
    });
    return results;
  }

  async getPlaceSuggestions(nameToSearch: string, originLat?: number, originLng?: number, maxResults: number = 2): Promise<{ placeId: string; description: string }[]> {
    // @ts-ignore
    const { AutocompleteSuggestion } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

    const request: google.maps.places.AutocompleteRequest = {
      input: nameToSearch,
      includedPrimaryTypes: ['geocode'],
      ...(originLat != null && originLng != null ? { locationBias: { lat: originLat, lng: originLng } } : {}),
    };
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    return (suggestions ?? [])
      .filter((suggestion: any) => !!suggestion.placePrediction)
      .slice(0, maxResults)
      .map((suggestion: any) => ({
        placeId: suggestion.placePrediction.toPlace().id,
        description: suggestion.placePrediction.text?.text ?? '',
      }));
  }

  async computeRoutes(origin: { lat: number, lng: number }, destination: { lat: number, lng: number }, waypoints: ILatLngPoint[] = []) {
    try {
      const chunks = this.buildRouteChunks(origin, destination, waypoints);
      const responses = await Promise.all(chunks.map((chunk) => this.requestRoute(chunk)));

      const failed = responses.find((data) => !data?.routes?.[0]);
      if (failed) return failed;

      return { routes: [{ legs: responses.flatMap((data) => data.routes[0].legs ?? []) }] };
    } catch (error) {
      // tslint:disable-next-line: no-console
      console.error(error);
      return [];
    }
  }

  private buildRouteChunks(
    origin: { lat: number, lng: number },
    destination: { lat: number, lng: number },
    waypoints: ILatLngPoint[]
  ): IRouteChunk[] {
    const points = [
      { latitude: origin.lat, longitude: origin.lng },
      ...waypoints,
      { latitude: destination.lat, longitude: destination.lng }
    ];

    const chunks: IRouteChunk[] = [];
    let start = 0;
    while (start < points.length - 1) {
      const end = Math.min(start + APP.ROUTE_WAYPOINTS_CHUNK_SIZE + 1, points.length - 1);
      chunks.push({
        origin: points[start],
        destination: points[end],
        intermediates: points.slice(start + 1, end)
      });
      start = end;
    }
    return chunks;
  }

  private async requestRoute(chunk: IRouteChunk) {
    const url = `https://routes.googleapis.com/directions/v2:computeRoutes?key=${environment.googleMapsApiKey}`;

    const body = {
      origin: { location: { latLng: chunk.origin } },
      destination: { location: { latLng: chunk.destination } },
      intermediates: chunk.intermediates.map((wp) => ({ location: { latLng: wp } })),
      travelMode: 'DRIVE',
      routingPreference: 'TRAFFIC_UNAWARE',
      computeAlternativeRoutes: false,
      optimizeWaypointOrder: false
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Goog-FieldMask': 'routes.legs' },
      body: JSON.stringify(body)
    });

    return response.json();
  }

  setupFullscreenButton(
    map: google.maps.Map,
    position: google.maps.ControlPosition,
    options: { marginTop?: string } = {}
  ): () => void {
    const marginTop = options.marginTop ?? '80px';

    const enterFullscreenSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" style="width:18px;height:18px;fill:#CCC"><path d="M0 0h6v2H2v4H0V0zm12 0h6v6h-2V2h-4V0zM0 12h2v4h4v2H0v-6zm16 4h-4v2h6v-6h-2v4z"/></svg>`;
    const exitFullscreenSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" style="width:18px;height:18px;fill:#CCC"><path d="M0 4h4V0h2v6H0V4zm12-4h2v4h4v2h-6V0zM0 12h6v6H4v-4H0v-2zm12 0h6v2h-4v4h-2v-6z"/></svg>`;
    const enterFullscreenSvgDark = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" style="width:18px;height:18px;fill:#666"><path d="M0 0h6v2H2v4H0V0zm12 0h6v6h-2V2h-4V0zM0 12h2v4h4v2H0v-6zm16 4h-4v2h6v-6h-2v4z"/></svg>`;
    const exitFullscreenSvgDark = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" style="width:18px;height:18px;fill:#666"><path d="M0 4h4V0h2v6H0V4zm12-4h2v4h4v2h-6V0zM0 12h6v6H4v-4H0v-2zm12 0h6v2h-4v4h-2v-6z"/></svg>`;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = 'Toggle fullscreen view';
    btn.style.cssText = `
      background: none padding-box rgb(255, 255, 255);
      border: 0;
      margin: 10px;
      padding: 0;
      cursor: pointer;
      user-select: none;
      border-radius: 2px;
      height: 40px;
      width: 40px;
      box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: ${marginTop};
    `;
    btn.innerHTML = enterFullscreenSvgDark;

    const mapDiv = map.getDiv();

    btn.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        mapDiv.requestFullscreen().then(() => {
          btn.innerHTML = exitFullscreenSvg;
        }).catch(() => {});
      } else {
        document.exitFullscreen().then(() => {
          btn.innerHTML = enterFullscreenSvg;
        }).catch(() => {});
      }
    });

    map.controls[position].push(btn);
    const streetView = map.getStreetView();

    const onFullscreenChange = () => {
      const isStreetViewVisible = streetView.getVisible();
      const isFullScreen = document.fullscreenElement;
      if (isStreetViewVisible) {
        btn.innerHTML = isFullScreen ? exitFullscreenSvg : enterFullscreenSvg;
      } else {
        btn.innerHTML = isFullScreen ? exitFullscreenSvgDark : enterFullscreenSvgDark;
      }
      btn.style.marginTop = isFullScreen ? '10px' : marginTop;
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);

    const streetViewListener = streetView.addListener('visible_changed', () => {
      const isStreetViewVisible = streetView.getVisible();
      if (isStreetViewVisible) {
        streetView.controls[position].push(btn);
      }
      btn.style.background = isStreetViewVisible ? 'rgb(68, 68, 68)' : 'rgb(255, 255, 255)';
      btn.innerHTML = isStreetViewVisible ? enterFullscreenSvg : enterFullscreenSvgDark;
    });

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      google.maps.event.removeListener(streetViewListener);
      btn.remove();
    };
  }
}
